import { getDateTimeForSorting } from 'shared/utils/dateUtils';

export type ChecklistEventSortFields = {
	id?: number | string;
	scheduledDate?: string;
	scheduledTime?: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	completedAt?: string | null;
};

export type ChecklistEventSortOrder = 'scheduledDateAsc' | 'completedDateDesc';

const getNumericId = (item: ChecklistEventSortFields): number => Number(item.id) || 0;

const getScheduledTimestamp = (item: ChecklistEventSortFields): number => {
	if (item.scheduledDateFormatted) {
		return getDateTimeForSorting(
			item.scheduledDateFormatted,
			item.scheduledTimeFormatted,
		).getTime();
	}
	return getDateTimeForSorting(item.scheduledDate, item.scheduledTime).getTime();
};

const getCompletedTimestamp = (item: ChecklistEventSortFields): number => {
	if (!item.completedAt) return 0;
	const parsed = Date.parse(String(item.completedAt));
	if (!Number.isNaN(parsed)) return parsed;
	return getDateTimeForSorting(item.completedAt).getTime();
};

/** Outstanding / past-due: earliest due first (same idea as to-do dueDateAsc). */
export function sortChecklistEventsByScheduledDateAsc<T extends ChecklistEventSortFields>(
	items: T[],
): T[] {
	return [...items].sort((a, b) => {
		const timeA = getScheduledTimestamp(a);
		const timeB = getScheduledTimestamp(b);
		const missingA = timeA === 0;
		const missingB = timeB === 0;
		if (missingA && !missingB) return 1;
		if (!missingA && missingB) return -1;
		const timeDiff = timeA - timeB;
		if (timeDiff !== 0) return timeDiff;
		return getNumericId(a) - getNumericId(b);
	});
}

/** Completed: most recently completed first (same idea as to-do completedDateDesc). */
export function sortChecklistEventsByCompletedDateDesc<T extends ChecklistEventSortFields>(
	items: T[],
): T[] {
	return [...items].sort((a, b) => {
		const timeDiff = getCompletedTimestamp(b) - getCompletedTimestamp(a);
		if (timeDiff !== 0) return timeDiff;
		return getNumericId(b) - getNumericId(a);
	});
}

export function sortChecklistEvents<T extends ChecklistEventSortFields>(
	items: T[],
	sortOrder: ChecklistEventSortOrder = 'scheduledDateAsc',
): T[] {
	return sortOrder === 'completedDateDesc'
		? sortChecklistEventsByCompletedDateDesc(items)
		: sortChecklistEventsByScheduledDateAsc(items);
}
